import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import { NewPageFormState } from '@/components/content-generator/editor/pages/components/types';

interface ResearchSummary {
  title: string;
  summary: string;
}

export const useClarifyingQuestions = (goalThreadId: string) => {
  const [questions, setQuestions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<string[]>(['', '']);
  const [error, setError] = useState<string | null>(null);
  const { executeAIOperation, isLoading } = useAIOperationWrapper();

  // Generic fallback questions if AI generation fails
  const GENERIC_FALLBACK_QUESTIONS = [
    "Who is your target audience and what specific outcome do you want them to achieve after reading this content?",
    "What are the most important points, examples, or details you want to ensure are included in this content?"
  ];

  const buildQuestionPrompt = (formData: NewPageFormState, researchSummaries: ResearchSummary[]) => {
    return `You are helping create better content by asking clarifying questions.

Based on this page request:
- Title: ${formData.title}
- Purpose: ${formData.initial_request}
- Type: ${formData.type}
- Writing Style: ${formData.writing_style || 'default'}
${researchSummaries.length > 0 ? `- Research Context: ${researchSummaries.length} documents attached` : ''}

Think step by step about what information would be most valuable to generate exceptional ${formData.type} content:
1. What critical context is missing from the user's request?
2. What information would make the biggest difference in content quality?
3. What would help you personalize this content for the specific audience?
4. What details would help address potential knowledge gaps?

Based on your analysis, generate exactly 2 concise, open-ended questions that will gather the MOST important additional context needed. These should be the highest-impact questions that will make the biggest difference in content quality.

Focus on specific details, examples, target audience nuances, and key points that would make this content more valuable and relevant.

Return only the 2 questions, numbered 1-2.`;
  };

  const parseQuestions = (content: string): string[] => {
    try {
      // Extract numbered questions from the response
      const lines = content.split('\n').filter(line => line.trim().length > 0);
      const questions: string[] = [];
      
      for (const line of lines) {
        const match = line.match(/^[1-2]\.?\s*(.+)$/);
        if (match && questions.length < 2) {
          questions.push(match[1].trim());
        }
      }
      
      // If we didn't get exactly 2 questions, fall back to generic ones
      if (questions.length !== 2) {
        console.warn('Failed to parse exactly 2 questions, using fallback');
        return GENERIC_FALLBACK_QUESTIONS;
      }
      
      return questions;
    } catch (error) {
      console.error('Question parsing failed:', error);
      return GENERIC_FALLBACK_QUESTIONS;
    }
  };

  const generateQuestions = useCallback(async (formData: NewPageFormState, researchSummaries: ResearchSummary[] = []) => {
    if (isLoading) return;
    
    setError(null);

    try {
      const questionPrompt = buildQuestionPrompt(formData, researchSummaries);

      const data = await executeAIOperation(
        async () => {
          const { data, error: functionError } = await supabase.functions.invoke('text-generation', {
            body: {
              message: questionPrompt,
              type: 'clarifying_questions',
              appName: 'Content Generator',
              functionName: 'Clarifying Questions Generation'
            }
          });

          if (functionError) {
            console.error('[useClarifyingQuestions] Edge function error:', functionError);
            throw new Error(functionError.message);
          }

          return data;
        },
        {
          immediate: true,
          errorPrefix: 'Clarifying questions generation failed',
          debug: false
        }
      );

      if (!data?.generatedText) {
        console.warn('[useClarifyingQuestions] No generated text returned, using fallback questions');
        setQuestions(GENERIC_FALLBACK_QUESTIONS);
        setAnswers(['', '']);
        return;
      }

      // Parse questions from the response
      const generatedQuestions = parseQuestions(data.generatedText);
      
      if (generatedQuestions.length === 0) {
        console.warn('[useClarifyingQuestions] No valid questions parsed, using fallback');
        setQuestions(GENERIC_FALLBACK_QUESTIONS);
      } else {
        setQuestions(generatedQuestions);
      }
      
      // Reset answers when new questions are generated
      setAnswers(['', '']);

    } catch (error: any) {
      console.error('[useClarifyingQuestions] Error generating questions:', error);
      setError(error.message || 'Failed to generate clarifying questions');
      
      // Fallback to generic questions on error
      setQuestions(GENERIC_FALLBACK_QUESTIONS);
      setAnswers(['', '']);
    }
  }, [isLoading, executeAIOperation]);

  const updateAnswer = useCallback((index: number, answer: string) => {
    setAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[index] = answer;
      return newAnswers;
    });
  }, []);

  const resetQuestions = useCallback(() => {
    setQuestions([]);
    setAnswers(['', '']);
    setError(null);
  }, []);

  return {
    questions,
    answers,
    updateAnswer,
    generateQuestions,
    resetQuestions,
    isGenerating: isLoading,
    error
  };
}; 